import {
  Activity,
  DataField,
  EnumObjects,
  Funnel,
  ITelemetryConfig,
  onNotification,
  SimpleTelemetryLogger,
  SuppressNexus,
  TelemetryEvent,
  UserAction
} from '@microsoft/oteljs';
import { ITelemetryBaseEvent, ITelemetryProperties } from '@fluidframework/common-definitions';
import { LogProcessor, LoggerContext, AriaInfo, UserContext } from './LogProcessor.types';
import {
  Audience,
  EventCategory,
  UserActionData,
  ActivityData,
  ActivityResultType,
  Event
} from '@fluidx/office-fluid-types';
import { FluidOneDsSink } from './OfficeFluidOneDsSink';
import { getOtelDataField } from './OtelDataFieldHelpers';

export class OtelLogProcessor implements LogProcessor {
  private readonly telemetryLogger: SimpleTelemetryLogger;
  private loadFullCalled = false;
  private readonly namespacePrefix: string;
  private telemetrySink: FluidOneDsSink | undefined;
  private readonly audience: Audience | undefined;

  constructor(ariaInfo: AriaInfo, private context: LoggerContext) {
    this.audience = context.audience;
    this.telemetryLogger = new SimpleTelemetryLogger(undefined, undefined, this.getTelemetryConfig());

    this.namespacePrefix = ariaInfo.namespacePrefix;
    this.telemetryLogger.setTenantToken(this.namespacePrefix, ariaInfo.token, SuppressNexus);

    // Listen to error notifications sent by Otel and log them to the console
    // for non production audience group
    if (this.isNonProductionAudience()) {
      onNotification().addListener((event) => {
        if (event.category === 0 && event.level === 0) {
          console.error(event.message());
        }
      });
    }
  }

  /**
   * If the audience is Automation, Local or Staging we consider it non-production for the purpose of telemetry event validation
   */
  private isNonProductionAudience = () => {
    if (this.audience === 'Automation' || this.audience === 'Local' || this.audience === 'Staging') {
      return true;
    }
    return false;
  };

  /**
   * Returns the telemetry config to be passed to the SimpleTelemetryLogger. By default we use Otel's ability to push events to a queue, an optimization useful when host applications want to delay load OneDs sink
   */
  private getTelemetryConfig(): ITelemetryConfig | undefined {
    const defaultTelemetryConfig = { enableQueue: true };
    if (!this.isNonProductionAudience()) {
      return { ...defaultTelemetryConfig, disableValidation: true };
    }
    return defaultTelemetryConfig;
  }

  /**
   * Method that logs the telemetry event based on the event category
   * @param event Telemetry event with namespace, category and a property bag of other fields
   */
  public async logEvent(event: Event) {
    if (event.category === EventCategory.UserAction) {
      this.logUserActionEventToOtel(event);
    } else if (event.category === EventCategory.Activity) {
      this.logActivityEventToOtel(event);
    } else {
      // If the logger hasn't been fully loaded but an error occurrs, load OneDs Sink.
      // This starts flushing the events and ensures telemetry for any crashes gets sent to the pipeline.
      if (event.category === EventCategory.Error && !this.loadFullCalled) {
        this.loadFull().catch(() => {
          // We do not need to explicitly handle the catch here as loadFull handles it internally
        });
      }
      this.logDefaultEventToOtel(event);
    }
  }

  public setUserContext(userContext: UserContext) {
    this.setPartAFields(userContext);
  }

  /**
   * Used to set the Session.EcsETag as a PartA Data Field. Needed to trace back which ECS Feature Rollouts and Experiments were used by the session.
   * @param sessionEcsETag string representing the ECS eTag
   */
  public setSessionEcsETag(sessionEcsETag: string) {
    this.setPartAFields({ sessionEcsETag });
  }

  public shutdown() {
    // If OneDsSink has not been fully loaded, call loadFull on it first then call shutdown
    this.loadFull()
      .then(() => this.telemetrySink?.shutdown())
      .catch(() => {
        // We do not need to explicitly handle the catch here as loadFull handles it internally
      });
  }

  /**
   * Loads the OneDS Sink, replays events in the queue on the sink so the events are sent for logging.
   * Invoking this for a second or higher time will be a no-op.
   */
  public async loadFull() {
    if (this.loadFullCalled) {
      return;
    }
    this.loadFullCalled = true;

    import(/* webpackChunkName: "officeFluidOneDsSink" */ './OfficeFluidOneDsSink')
      .then((m) => {
        this.telemetrySink = new m.OfficeFluidOneDsSink(this.context);
        this.telemetryLogger.addSink(this.telemetrySink.createOneDsSink());
        this.telemetryLogger.flushQueue();
      })
      .catch((error) => {
        this.logDefaultEventToOtel({
          category: EventCategory.Error,
          props: { eventName: 'LoggerLoadFailure', message: error.message, stack: error.stack }
        });
      });
  }

  private setPartAFields(newPartAFields: Partial<LoggerContext>) {
    // We update the OtelLogProcessor's context, to ensure full context is replayed if this method is called before loadFull was called
    // loadFull creates the telemetry sink and provides the context to it, so those fields can be added as Part A fields when the sink is created
    this.context = { ...this.context, ...newPartAFields };
    this.telemetrySink?.setPartADataFields(this.context);
  }

  private getDataFieldsFromProps(props: ITelemetryProperties) {
    let dataFields: DataField[] = [];
    Object.keys(props).forEach((key) => {
      const dataField: DataField | undefined = getOtelDataField(props[key], key);
      if (!dataField) {
        return;
      }
      dataFields.push(dataField);
    });
    return dataFields;
  }

  private logDefaultEventToOtel(event: Event) {
    this.logEventToOtelInternal(event, (telemetryEvent) => {
      this.prepareDefaultOtelEvent(telemetryEvent, event);
    });
  }

  private logActivityEventToOtel(event: Event) {
    this.logEventToOtelInternal(event, (telemetryEvent) => {
      // Sending props to make sure that fields that are needed for the activity are present
      this.prepareActivityOtelEvent(telemetryEvent, {
        ...event.props
      } as ITelemetryBaseEvent);
    });
  }

  private logUserActionEventToOtel(event: Event) {
    this.logEventToOtelInternal(event, (telemetryEvent) => {
      // Sending props to make sure that fields that are needed for the userAction are present
      this.prepareUserActionOtelEvent(
        telemetryEvent,
        {
          ...event.props
        } as ITelemetryBaseEvent,
        event.namespace
      );
    });
  }

  private logEventToOtelInternal(event: Event, prepareEvent: (telemetryEvent: TelemetryEvent) => void) {
    const telemetryEvent = this.getBasicOtelEvent(event);
    prepareEvent(telemetryEvent);
    this.telemetryLogger.sendTelemetryEvent(telemetryEvent);
  }

  private getBasicOtelEvent(event: Event): TelemetryEvent {
    // Otel APIs will fail if the last part of the eventName is not uppercase
    let eventType = event.category && `${event.category.charAt(0).toUpperCase()}${event.category.substring(1)}`;

    // subCategory is appended at the end of the Table name in case of Generic events
    // If a subCategory is specified for any event other than a generic event, it will simply be logged as a column in the table
    if (event.props.subCategory && event.category === EventCategory.Generic) {
      eventType =
        eventType +
        `.${(event.props.subCategory as string).charAt(0).toUpperCase()}${(event.props.subCategory as string).substring(
          1
        )}`;
    }

    return {
      eventName: `${this.namespacePrefix}${event.namespace ? '.' + event.namespace : ''}.${eventType}`,
      eventFlags: {
        diagnosticLevel: EnumObjects.DiagnosticLevel.RequiredServiceData
      }
    };
  }

  private prepareDefaultOtelEvent(telemetryEvent: TelemetryEvent, event: Event) {
    telemetryEvent.eventFlags!.dataCategories = EnumObjects.DataCategories.ProductServicePerformance;
    telemetryEvent.dataFields = this.getDataFieldsFromProps({ ...event.props });
  }

  private mapActivityResultTypeToCode(resultType: ActivityResultType): number {
    switch (resultType) {
      case ActivityResultType.Failure:
        return 0;
      case ActivityResultType.Success:
        return 1;
      case ActivityResultType.Cancelled:
        return 2;
    }
  }

  private prepareBaseActivityOtelEvent(telemetryEvent: TelemetryEvent, props: ActivityData, fromUserAction: boolean) {
    if (!props.durationMs) {
      props.durationMs = 0;
    }

    // Otel's Activity contract expects duration in microSeconds (not milliseconds), and expects it to be a round value.
    const durationInMicroseconds = Math.floor(props.durationMs * 1000);

    const { durationMs, succeeded, resultType, ...propsWithoutActivityData } = props;

    telemetryEvent.eventContract = {
      name: Activity.contractName,
      dataFields: Activity.getFields({
        duration: durationInMicroseconds,
        count: 1,
        aggMode: 0,
        success: succeeded,
        result:
          !fromUserAction && resultType
            ? { code: this.mapActivityResultTypeToCode(resultType), type: resultType }
            : undefined
      })
    };
    // returning new Props to make sure that we don't lose the fields in props that are not used for the activity
    return propsWithoutActivityData;
  }

  private prepareActivityOtelEvent(telemetryEvent: TelemetryEvent, props: ITelemetryBaseEvent) {
    const eventName = props.eventName;

    // Each activity is bucketed into its own eventName. Otel APIs will fail if the eventName doesn't start with a capital letter
    telemetryEvent.eventName += `.${eventName.charAt(0).toUpperCase()}${eventName.substring(1)}`;
    telemetryEvent.eventFlags!.dataCategories = EnumObjects.DataCategories.ProductServicePerformance;
    const unusedProps = this.prepareBaseActivityOtelEvent(telemetryEvent, props, false);

    telemetryEvent.dataFields = this.getDataFieldsFromProps(unusedProps || {});
  }

  private prepareUserActionOtelEvent(telemetryEvent: TelemetryEvent, props: ITelemetryBaseEvent, namespace?: string) {
    const userActionEventName = props.eventName;
    // Each user action is bucketed into its own eventName. Otel APIs will fail if the eventName doesn't start with a capital letter
    telemetryEvent.eventName += `.${userActionEventName.charAt(0).toUpperCase()}${userActionEventName.substring(1)}`;
    telemetryEvent.eventFlags!.dataCategories = EnumObjects.DataCategories.ProductServiceUsage;

    /**
     * To create Seen/Tried/Successful user funnels, events need to contain special datafields generated from the Funnel contract. These fields are of the form Funnel_Name and Funnel_State.
     * Funnel_Name is an optional name of the feature/operation and Funnel_State defines whether an event is considered Seen/Tried/Successful
     * An event is considered Seen, when the user is exposed to fluid functionality, for example seeing a Fluid component insertion icon in Teams chat
     * An event is considered Tried, when the user tries inserting a Fluid component or loads a component shared with them
     * An event is considered Successful, when the user edits the component, for example inserts an atmention
     * All user actions are considered Successful by default
     * More details https://microsoft.sharepoint-df.com/:x:/t/OXODIG-Team/EbnL-NDqI8lGiAY9bidsVIABiDUE1xlj8VBO5TNNjMmwNg?e=jkhObs
     */
    const userFunnelDataFields = Funnel.getFields('Funnel', {
      name: namespace,
      state: 'Successful'
    });

    const userData = props as UserActionData;

    // Add in the user action contract-specific fields
    const userActionEventDataFields = UserAction.getFields({
      id: userData.actionId,
      name: userActionEventName,
      commandSurface: userData.commandSurface,
      parentName: userData.parentName,
      triggerMethod: userData.triggerMethod,
      timeOffsetMs: userData.timeOffsetMs
    });

    const propsWithoutActivityData = this.prepareBaseActivityOtelEvent(telemetryEvent, props, true);

    const {
      eventName,
      actionId,
      commandSurface,
      parentName,
      triggerMethod,
      timeOffsetMs,
      ...unusedProps
    } = propsWithoutActivityData;

    telemetryEvent.dataFields = [
      ...userFunnelDataFields,
      ...userActionEventDataFields,
      ...this.getDataFieldsFromProps(unusedProps || {})
    ];
  }
}

// cspell:ignore otel, oteljs
